// ─── Shared helpers ───────────────────────────────────────────────────
// Auth is cookie-based: the provider sets an HttpOnly session cookie on
// login, so every request just needs `credentials: 'include'`. There is no
// bearer token in JS.
const API_BASE = '/api'

function jsonHeaders(): HeadersInit {
  return { 'Content-Type': 'application/json' }
}

function handle401(): void {
  if (!window.location.pathname.startsWith('/login')) {
    window.location.href = '/login'
  }
}

async function handleResponse<T>(response: Response, defaultError: string): Promise<T> {
  if (response.status === 401) {
    handle401()
    throw new Error('Unauthorized')
  }
  if (!response.ok) {
    let message = defaultError
    try {
      const errorData = await response.json()
      message = errorData.error || errorData.message || defaultError
    } catch {
      // fall back to defaultError
    }
    throw new Error(message)
  }
  return response.json()
}

function checkVoidResponse(response: Response, defaultError: string): void {
  if (response.status === 401) {
    handle401()
    throw new Error('Unauthorized')
  }
  if (!response.ok) throw new Error(defaultError)
}

// ─── Auth ─────────────────────────────────────────────────────────────
export interface CurrentUser {
  id: string
  email: string | null
  displayName: string | null
}

export async function login(email: string, password: string): Promise<CurrentUser> {
  const response = await fetch(`${API_BASE}/login`, {
    method: 'POST',
    credentials: 'include',
    headers: jsonHeaders(),
    body: JSON.stringify({ email, password }),
  })
  return handleResponse(response, 'Login failed')
}

export async function signup(email: string, password: string): Promise<CurrentUser> {
  const response = await fetch(`${API_BASE}/signup`, {
    method: 'POST',
    credentials: 'include',
    headers: jsonHeaders(),
    body: JSON.stringify({ email, password }),
  })
  return handleResponse(response, 'Signup failed')
}

/** Returns the current session user, or null if not signed in. */
export async function getMe(): Promise<CurrentUser | null> {
  const response = await fetch(`${API_BASE}/me`, { credentials: 'include' })
  if (response.status === 401) return null
  if (!response.ok) throw new Error('Failed to load session')
  return response.json()
}

export async function logout(): Promise<void> {
  await fetch(`${API_BASE}/logout`, { method: 'POST', credentials: 'include' })
}

// ─── Passkeys ────────────────────────────────────────────────────────
export interface PasskeySummary {
  id: string
  nickname: string
  createdAt: string
  lastUsedAt: string | null
  isBackedUp: boolean
}

export interface PasskeyOptionsEnvelope<T> {
  sessionId: string
  options: T
}

export async function passkeyRegisterOptions(): Promise<PasskeyOptionsEnvelope<unknown>> {
  const response = await fetch(`${API_BASE}/passkeys/register/options`, {
    method: 'POST',
    credentials: 'include',
  })
  return handleResponse(response, 'Failed to start passkey registration')
}

export async function passkeyRegisterVerify(
  sessionId: string,
  credential: unknown,
  nickname?: string
): Promise<PasskeySummary> {
  const response = await fetch(`${API_BASE}/passkeys/register/verify`, {
    method: 'POST',
    credentials: 'include',
    headers: jsonHeaders(),
    body: JSON.stringify({ sessionId, credential, nickname }),
  })
  return handleResponse(response, 'Passkey registration failed')
}

export async function passkeyLoginOptions(): Promise<PasskeyOptionsEnvelope<unknown>> {
  const response = await fetch(`${API_BASE}/passkeys/authenticate/options`, {
    method: 'POST',
    credentials: 'include',
    headers: jsonHeaders(),
  })
  return handleResponse(response, 'Failed to start passkey sign-in')
}

export async function passkeyLoginVerify(sessionId: string, credential: unknown): Promise<{ ok: boolean }> {
  const response = await fetch(`${API_BASE}/passkeys/authenticate/verify`, {
    method: 'POST',
    credentials: 'include',
    headers: jsonHeaders(),
    body: JSON.stringify({ sessionId, credential }),
  })
  return handleResponse(response, 'Passkey sign-in failed')
}

export async function listPasskeys(): Promise<PasskeySummary[]> {
  const response = await fetch(`${API_BASE}/passkeys`, { credentials: 'include' })
  return handleResponse(response, 'Failed to load passkeys')
}

export async function renamePasskey(id: string, nickname: string): Promise<void> {
  const response = await fetch(`${API_BASE}/passkeys/${encodeURIComponent(id)}`, {
    method: 'PATCH',
    credentials: 'include',
    headers: jsonHeaders(),
    body: JSON.stringify({ nickname }),
  })
  checkVoidResponse(response, 'Failed to rename passkey')
}

export async function deletePasskey(id: string): Promise<void> {
  const response = await fetch(`${API_BASE}/passkeys/${encodeURIComponent(id)}`, {
    method: 'DELETE',
    credentials: 'include',
  })
  checkVoidResponse(response, 'Failed to delete passkey')
}
